// @flow
import * as React from 'react';
import Downshift from 'downshift';
import classnames from 'classnames';
import VirtualList from 'react-tiny-virtual-list';
import { connect } from 'react-redux';
import { selectClient } from './clientsActions';
import matchSorter from 'match-sorter';
import AsyncValues from 'lib/async-value';

const itemToString = item => (item ? item.merchant_group_name : '');
const remToPixels = (multiplier: number) => multiplier * 0.82 * 16;

type StateProps = {
  isLoading: boolean,
  hidden: boolean,
  disabled: ?boolean,
  selectedItem: ?MerchantGroupBlob,
  allItems: Array<MerchantGroupBlob>,
};

type OwnProps = {
  hideButton?: boolean,
};

type DispatchProps = {
  selectClient: typeof selectClient,
};

type Props = StateProps & DispatchProps & OwnProps;
type State = {
  items: Array<MerchantGroupBlob>,
  inputValue: string,
  highlightedIndex: ?number,
};

export class ClientSelectMenu extends React.Component<Props, State> {
  state = {
    items: this.props.allItems,
    inputValue: '',
    highlightedIndex: null,
  };

  componentWillReceiveProps(nextProps: Props) {
    if (
      nextProps.allItems !== this.props.allItems &&
      Array.isArray(nextProps.allItems)
    ) {
      this.setState({
        items: nextProps.allItems,
      });
    }
  }

  getItems(items: Array<MerchantGroupBlob>, inputValue: ?string) {
    if (inputValue != null && typeof inputValue === 'string') {
      return matchSorter(items, inputValue, {
        keys: [
          'merchant_group_name',
          {
            maxRanking: matchSorter.rankings.CASE_SENSITIVE_EQUAL,
            key: 'merchant_group_id',
          },
        ],
      });
    }

    return items;
  }

  handleStateChange = (changes: Object, downshiftState: Object) => {
    if (changes.hasOwnProperty('isOpen')) {
      this.setState({
        items: this.props.allItems,
        inputValue: '',
        highlightedIndex: null,
      });
    } else if (changes.hasOwnProperty('inputValue')) {
      const { inputValue } = changes;
      this.setState({
        items: this.getItems(this.props.allItems, inputValue),
        inputValue,
        highlightedIndex: 0,
      });
    } else if (changes.hasOwnProperty('highlightedIndex')) {
      this.setState({
        highlightedIndex: changes.highlightedIndex,
      });
    }
  };

  handleChange = (selectedItem: any, downshiftState: Object) => {
    this.setState({ items: this.props.allItems, inputValue: '' });
    this.props.selectClient(selectedItem.merchant_group_id);
  };

  render() {
    const {
      isLoading,
      selectedItem,
      hidden,
      disabled,
      hideButton,
    } = this.props;
    if (hidden || isLoading || !this.state.items) return null;

    const itemHeight = remToPixels(2.5);
    const searchFormHeight = remToPixels(2.5 + 2);
    const menuHeight = remToPixels(2 + 2.5 * 10);
    const virtualListHeight = menuHeight - searchFormHeight;

    const items = this.state.items;
    const toggleClassName = classnames(
      'button-reset pointer cb nowrap lh-solid pv2 ph0 ml3 bg-animate bn br2 outline-0 bg-transparent gray',
      'ClientSelectMenu__toggle',
      { 'hover-blue': disabled !== true }
    );
    const menuClassName =
      'list pl0 pv0 ma0 bg-white ba0 b--moon-gray br2 nt1 z-999 absolute w5 overflow-hidden';
    const menuStyle = {
      top: hideButton ? 0 : '3rem',
      boxShadow: '0 0 1px hsla(0,0%,7%,.3), 0 10px 25px -4px hsla(0,0%,7%,.4)',
      width: hideButton ? '30rem' : '24rem',
      height: items.length ? menuHeight : 3 * itemHeight,
    };

    return (
      <Downshift
        itemToString={itemToString}
        items={items}
        itemCount={items.length}
        onStateChange={this.handleStateChange}
        onChange={this.handleChange}
        inputValue={this.state.inputValue}
        selectedItem={selectedItem}
        highlightedIndex={this.state.highlightedIndex}
        isOpen={hideButton === true ? true : undefined}
        id="client-select"
      >
        {({
          getInputProps,
          getLabelProps,
          getButtonProps,
          getItemProps,
          toggleMenu,
          isOpen,
          selectedItem,
          // inputValue,
          highlightedIndex,
          itemToString,
        }) => (
          <div className="relative ClientSelectMenu" id="client-select-menu">
            {hideButton === true ? null : (
              <button
                className={toggleClassName}
                onClick={toggleMenu}
                type="button"
                disabled={disabled}
                {...getButtonProps({
                  onBlur: e => e.preventDefault(),
                })}
              >
                <ClientIcon
                  className="v-mid mr2"
                  style={{ height: '1.35rem', width: '1.35rem' }}
                />
                <span
                  className="v-mid mr2 truncate mw5-l mw4 dib ClientSelectMenu__label"
                  {...getLabelProps()}
                >
                  {selectedItem ? selectedItem.merchant_group_name : ''}
                </span>
                {disabled !== true && (
                  <i
                    className="fa v-mid fa-angle-down"
                    aria-hidden="true"
                    style={{
                      fontSize: '1.8rem',
                      transform: 'scaleX(0.9)',
                    }}
                  />
                )}
              </button>
            )}
            {isOpen && (
              <div className={menuClassName} style={menuStyle}>
                <form className="pa3 relative">
                  <input
                    type="text"
                    className="w-100 input-reset ba b--light-gray db db pl2 pr4 ClientSelectMenu__input"
                    style={{ height: itemHeight }}
                    {...getInputProps({
                      autoFocus: true,
                      placeholder: hideButton ? 'Type to Search...' : undefined,
                    })}
                  />
                  <i
                    className="fa fa-search absolute dib silver"
                    aria-hidden="true"
                    style={{
                      top: '50%',
                      right: '1.7rem',
                      transform: 'translateY(-50%)',
                    }}
                  />
                </form>
                <div
                  className="absolute right-0 left-0 bottom-0 overflow-hidden b--light-gray bt bt1"
                  style={{ top: searchFormHeight }}
                >
                  {items.length ? (
                    <VirtualList
                      width={'100%'}
                      scrollToIndex={highlightedIndex || 0}
                      scrollToAlignment="auto"
                      height={
                        items.length < 5
                          ? items.length * itemHeight
                          : virtualListHeight
                      }
                      itemCount={items.length}
                      itemSize={itemHeight}
                      renderItem={({ index, style }) => {
                        const item = items[index];

                        return (
                          <button
                            className={getItemClassName({
                              highlighted: highlightedIndex === index,
                              selected: selectedItem === items[index],
                            })}
                            style={{ height: itemHeight, ...style }}
                            key={item.merchant_group_id}
                            {...getItemProps({ item, index })}
                          >
                            {item.merchant_group_name}
                          </button>
                        );
                      }}
                    />
                  ) : (
                    <span
                      className="db tc gray"
                      style={{
                        height: itemHeight,
                        lineHeight: itemHeight + 'px',
                      }}
                    >
                      No results
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </Downshift>
    );
  }
}

const mapStateToProps = (state, ownProps: OwnProps): StateProps => {
  const { clients, currentClient: selected, showMenu } = state.clients;
  const disabled = isMenuDisabled(selected, clients);
  const hidden =
    showMenu === false ||
    !Array.isArray(clients) ||
    (ownProps.hideButton !== true && selected == null);

  return {
    isLoading: clients === AsyncValues.Pending,
    allItems: clients,
    disabled,
    selectedItem: Array.isArray(clients)
      ? clients.find(i => i.merchant_group_id === selected)
      : null,
    hidden,
  };
};

export default connect(mapStateToProps, { selectClient })(ClientSelectMenu);

function isMenuDisabled(selected: *, clients: *) {
  if (selected != null && Array.isArray(clients) && clients.length === 1) {
    return true;
  }

  return undefined;
}

function getItemClassName({
  highlighted,
  selected,
}: {
  highlighted: boolean,
  selected: boolean,
}) {
  return classnames(
    'button-reset b--none db mv0 ph3 bg-animate gray over-bg-near-white  hover-mid-gray tl w-100 truncate',
    {
      'bg-white': highlighted === false,
      'bg-near-white mid-gray': highlighted === true,
      b: selected === true,
    }
  );
}

function ClientIcon(props: *) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 360" {...props}>
      <path
        fill="currentColor"
        d="M328.1 68.4h-14.8V53.6h-29.6v14.8H247V24H113.8v44.4H76.3V53.6H46.7v14.8H31.9C15.6 68.4 2.3 81.7 2.3 98v207.3c0 16.3 13.3 29.6 29.6 29.6h296.2c16.3 0 29.6-13.3 29.6-29.6V98c0-16.3-13.3-29.6-29.6-29.6zM143.4 53.6h74v14.8h-74V53.6zm184.7 251.7H31.9v.1V98h296.2v207.3z"
      />
    </svg>
  );
}



// WEBPACK FOOTER //
// ./src/containers/client/ClientSelectMenu.js